import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Backdrop,
  CircularProgress,
  Chip,
  Grid,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import darkTheme from "../../style/theme";
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import AccessLockModal from "../../components/security/AccessLockModal";
import { Toggle as OpusToggle, Button as OpusButton, SearchInput as OpusSearchInput, Icon as OpusIcon, IconButton as OpusIconButton, LoadingState, Select as OpusSelect } from "../../components/common/opus";
import { AuthContext } from "../../contexts/AuthContext";
import SceneList from "../../components/scenes/SceneList";
import SceneForm from "../../components/scenes/SceneForm";
import VersionDialog from "../../components/scenes/VersionDialog";
import ViewSceneDetails from "../../components/scenes/ViewSceneDetails.jsx";
import SceneImportDialog from "../../components/SceneImportDialog.jsx";
import { useAlert } from "../../contexts/AlertContext.js";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useActivity, ACTIVITY_TYPES, RESOURCE_TYPES } from "../../contexts/ActivityTrackingContext";
import { useSession } from "../../contexts/SessionContext";
import BusinessIcon from "@mui/icons-material/Business";
import apiService from "../../services/api.service";
import { useNavigate } from "react-router-dom";
import { DEFAULT_LAYER, fromApiCaption, toCaptionPayload } from "../../services/scenes/captionPayload";
const SceneManagement = () => {
  const navigate = useNavigate();
  const [scenes, setScenes] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [importOpen, setImportOpen] = useState(false); // OPUS-907 cross-env import
  const [resumeJobs, setResumeJobs] = useState([]); // OPUS-907 incomplete import jobs
  const [resumeJob, setResumeJob] = useState(null); // the job being resumed (null = fresh import)
  const [resumeDismissed, setResumeDismissed] = useState(false); // OPUS-913: dismissable resume banner
  const { selectedOrgId: sessionOrgId, updateSelectedOrgId } = useSession();
  const [selectedOrgId, setSelectedOrgId] = useState(sessionOrgId || "");
    const [sceneOrg, setSceneOrg] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [open, setOpen] = useState(false);
  const { userInfo } = useContext(AuthContext);
  const userRole = userInfo?.data?.role?.name;
  const userOrg = userInfo?.data?.organization_id?._id;
  const isSuperAdmin = userRole === "super_admin";
  const [pageLoading, setPageLoading] = useState(true);
  const [newScene, setNewScene] = useState({
    scene_name: "",
    organization_id: "",
    id: "",
    captions: [],
  });
  const [versionMenu, setVersionMenu] = useState({
    open: false,
    anchorEl: null,
    captionIndex: -1,
    versions: [],
  });
  const [editMode, setEditMode] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedScene, setSelectedScene] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [versionDialog, setVersionDialog] = useState({
    open: false,
    captionIndex: -1,
    isMajorChange: false,
    file: null,
  });
  const [dataStatus, setDataStatus] = useState({
    orgs: false,
    scenes: false,
  });
  const [selectedScenes, setSelectedScenes] = useState([]);
  const [lockModal, setLockModal] = useState({ open: false, id: null, name: "" }); // OPUS-913 access lock
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const alert = useAlert();
  const { trackActivity } = useActivity();
  const [showOnlyMyScenes, setShowOnlyMyScenes] = useState(false);
  const [sceneSearch, setSceneSearch] = useState("");
  const [userToggledSceneFilter, setUserToggledSceneFilter] = useState(false);
  const [filteredScenes, setFilteredScenes] = useState([])
  // OPUS-1148: scene-list filter/sort parity with the renderer list.
  const [sceneSortBy, setSceneSortBy] = useState("newest");
  const [sceneCreatorFilter, setSceneCreatorFilter] = useState("all");


  useEffect(() => {
    if (dataStatus.orgs && dataStatus.scenes) {
      setPageLoading(false);
    }
  }, [dataStatus]);

  useEffect(() => {
    if (sessionOrgId) {
      setSelectedOrgId(sessionOrgId);
    }
  }, [sessionOrgId]);

  // OPUS-907: surface incomplete cross-env imports so they can be resumed.
  const refreshResumeJobs = useCallback(() => {
    if (!isSuperAdmin) return;
    apiService.listSceneImportJobs()
      .then((resp) => {
        const jobs = (resp?.data || []).filter(
          (j) => j.status !== "completed" && j.progress && j.progress.pending > 0
        );
        setResumeJobs(jobs);
      })
      .catch(() => {});
  }, [isSuperAdmin]);

  useEffect(() => { refreshResumeJobs(); }, [refreshResumeJobs]);
  
  useEffect(() => {
    apiService.getOrganizationsFromScene()
      .then((data) => {
        setOrganizations(data);
        if (!isSuperAdmin && userOrg) {
          setSelectedOrgId(userOrg);
          updateSelectedOrgId(userOrg);
        } else if (sessionOrgId && data.some(org => org._id === sessionOrgId)) {
          setSelectedOrgId(sessionOrgId);
        } else if (data && data.length > 0) {
          setSelectedOrgId(data[0]._id);
          updateSelectedOrgId(data[0]._id);
        }
        setDataStatus((prev) => ({ ...prev, orgs: true }));
      })
      .catch((error) => {
        console.error("Error fetching organizations:", error);
        setDataStatus((prev) => ({ ...prev, orgs: true }));
      });
  }, [isSuperAdmin, userOrg, sessionOrgId, updateSelectedOrgId]);

  useEffect(() => {
    if (selectedOrgId) {
      setLoading(true);
      apiService.getScenesByOrganization(selectedOrgId)
        .then((data) => {
          if (data && data.scenes) {
            setScenes(data.scenes);
            setSceneOrg(data.organization);
          } else {
            setScenes([]);
          }
          setLoading(false);
          setDataStatus((prev) => ({ ...prev, scenes: true }));
        })
        .catch((error) => {
          console.error("Error fetching scenes for organization:", error);
          setScenes([]);
          setLoading(false);
          setDataStatus((prev) => ({ ...prev, scenes: true }));
        });
    }
  }, [selectedOrgId]);

  useEffect(() => {
    if (scenes.length > 0) {
      let filtered = [...scenes];
      
      if (showOnlyMyScenes && userInfo?.data?._id) {
        filtered = filtered.filter(scene =>
          scene.created_by === userInfo.data._id ||
          (scene.created_by && scene.created_by._id === userInfo.data._id)
        );

        if (filtered.length === 0) {
          setShowOnlyMyScenes(false);
          filtered = [...scenes];
          alert.info("You don't have any scenes associated with your account");
        }
      }

      if (sceneCreatorFilter !== "all") {
        filtered = filtered.filter((s) => (s.created_by?._id || s.created_by) === sceneCreatorFilter);
      }

      const q = sceneSearch.trim().toLowerCase();
      if (q) {
        filtered = filtered.filter((scene) =>
          (scene.scene_name || scene.name || "").toLowerCase().includes(q)
        );
      }

      // OPUS-1148: sort parity with the renderer list.
      const ts = (s) => new Date(s.created_at || s.createdAt || 0).getTime();
      const nameOf = (s) => String(s.scene_name || s.name || "");
      // OPUS-1158: the creator NAME lives on created_by_user (same field the
      // SceneList "Created By" column uses); created_by is just the id.
      const creatorOf = (s) => String(s.created_by_user?.name || s.created_by?.name || s.created_by?.user_name || s.created_by?._id || s.created_by || "");
      filtered = [...filtered].sort((a, b) => {
        switch (sceneSortBy) {
          case "oldest": return ts(a) - ts(b);
          case "name": return nameOf(a).localeCompare(nameOf(b));
          case "creator": return creatorOf(a).localeCompare(creatorOf(b));
          case "newest":
          default: return ts(b) - ts(a);
        }
      });

      setFilteredScenes(filtered);
    } else {
      setFilteredScenes([]);
    }
  }, [scenes, showOnlyMyScenes, userInfo?.data?._id, sceneSearch, sceneCreatorFilter, sceneSortBy]);

  // OPUS-1158: distinct creators for the scene-list filter. Label from
  // created_by_user.name (the field the "Created By" column resolves) so the
  // dropdown shows names, not raw ids; value stays the id used by the filter.
  const sceneCreatorOptions = (() => {
    const seen = new Map();
    (scenes || []).forEach((s) => {
      const id = s.created_by?._id || (typeof s.created_by === "string" ? s.created_by : null);
      if (!id) return;
      const label = s.created_by_user?.name || s.created_by?.name || s.created_by?.user_name;
      // Prefer a real name: fill/upgrade the label if we haven't got one yet.
      if (!seen.has(id) || (label && seen.get(id) === id)) seen.set(id, label || id);
    });
    return [{ value: "all", label: "All creators" }, ...Array.from(seen, ([value, label]) => ({ value, label }))];
  })();


  const handleSceneFilterChange = (showOnlyMine) => {
    setShowOnlyMyScenes(showOnlyMine);
    setUserToggledSceneFilter(true);
    
    if (showOnlyMine) {
      const userScenes = scenes.filter(scene => 
        scene.created_by === userInfo.data._id || 
        (scene.created_by && scene.created_by._id === userInfo.data._id)
      );
      
      if (userScenes.length === 0) {
        alert.info("You don't have any scenes associated with your account");
      }
    }
    
    trackActivity(
      ACTIVITY_TYPES.READ,
      RESOURCE_TYPES.SCENE,
      'filter',
      `${showOnlyMine ? 'Showing' : 'Not showing'} only my scenes`
    );
  };
  
  const handleOrgSelect = (event) => {
    const newOrgId = event.target.value;
    setSelectedOrgId(newOrgId);
    updateSelectedOrgId(newOrgId); // This should save to localStorage via context
    setDataStatus((prev) => ({ ...prev, scenes: false }));
  };

  const handleClickOpen = () => {
    const orgId = selectedOrgId || organizations[0]?._id;
    if (!orgId) {
      alert.info("Please select an organization first.");
      return;
    }
    navigate(`/dashboard/scene-management/${orgId}/new/edit`);
  };

  const handleClose = () => {
    setOpen(false);
    setNewScene({
      scene_name: "",
      organization_id: "",
      captions: [],
    });
    setIsEditing(false);
  };

  const handleNewSceneChange = (event) => {
    const { name, value } = event.target;
    setNewScene((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddCaption = () => {
    setNewScene((prev) => ({
      ...prev,
      captions: [
        ...prev.captions,
        {
          caption_name: "",
          description: "",
          version: "v1.0",
          file: null,
          id: "",
          // OPUS-1391: a new caption lands on the default layer.
          layer: DEFAULT_LAYER,
        },
      ],
    }));
  };
  const handleLockScene = useCallback(async (scene) => {
    try {
      setLoading(true);
      const sceneId = scene._id || scene.id;
      await apiService.lockScene(sceneId);
      
      setScenes(prevScenes => prevScenes.map(s => {
        if ((s._id || s.id) === sceneId) {
          return {
            ...s,
            locked: {
              lockedStatus: true,
              lockedBy: userInfo.data._id, 
              lockedAt: new Date().toISOString()
            }
          };
        }
        return s;
      }));
      
      trackActivity(
        ACTIVITY_TYPES.UPDATE,
        RESOURCE_TYPES.SCENE,
        sceneId,
        scene.name || scene.scene_name,
        {
          organization_id: selectedOrgId,
          organization_name: organizations.find(org => org._id === selectedOrgId)?.name,
          action: "locked",
        }
      );
      
      alert.success(`Scene "${scene.name || scene.scene_name}" locked successfully`);
    } catch (error) {
      console.error("Error locking scene:", error);
      alert.error(error.message || "Failed to lock scene. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [userInfo, trackActivity, selectedOrgId, organizations, alert]);
  
  const handleUnlockScene = useCallback(async (scene) => {
    try {
      setLoading(true);
      const sceneId = scene._id || scene.id;
      await apiService.unlockScene(sceneId);
      
      setScenes(prevScenes => prevScenes.map(s => {
        if ((s._id || s.id) === sceneId) {
          return {
            ...s,
            locked: {
              lockedStatus: false,
              lockedBy: null,
              lockedAt: null
            }
          };
        }
        return s;
      }));
      
      trackActivity(
        ACTIVITY_TYPES.UPDATE,
        RESOURCE_TYPES.SCENE,
        sceneId,
        scene.name || scene.scene_name,
        {
          organization_id: selectedOrgId,
          organization_name: organizations.find(org => org._id === selectedOrgId)?.name,
          action: "unlocked",
        }
      );
      
      alert.success(`Scene "${scene.name || scene.scene_name}" unlocked successfully`);
    } catch (error) {
      console.error("Error unlocking scene:", error);
      alert.error(error.message || "Failed to unlock scene. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [userInfo, trackActivity, selectedOrgId, organizations, alert]);

  const handleCaptionChange = (index, field, value) => {
    const updatedCaptions = [...newScene.captions];
    updatedCaptions[index][field] = value;
    if (field === "caption_name") {
      updatedCaptions[index].id = `${value.replace(/\s+/g, "_")}`;
    }
    setNewScene((prev) => ({ ...prev, captions: updatedCaptions }));
  };

  const handleFileChange = (index, file) => {
    if (isEditing && newScene.captions[index].existingFile) {
      setVersionDialog({
        open: true,
        captionIndex: index,
        isMajorChange: false,
        file: file,
      });
    } else {
      const updatedCaptions = [...newScene.captions];
      updatedCaptions[index].file = file;
      setNewScene((prev) => ({ ...prev, captions: updatedCaptions }));
    }
  };

  const handleVersionDialogClose = () => {
    setVersionDialog({
      open: false,
      captionIndex: -1,
      isMajorChange: false,
      file: null,
    });
  };

  const handleVersionChangeType = (isMajor) => {
    setVersionDialog((prev) => ({
      ...prev,
      isMajorChange: isMajor,
    }));
  };

  const handleVersionConfirm = () => {
    const { captionIndex, isMajorChange, file } = versionDialog;
    if (captionIndex === -1 || !file) return;
    const updatedCaptions = [...newScene.captions];
    const caption = updatedCaptions[captionIndex];
    const versionMatch = caption.version.match(/v(\d+)\.(\d+)/);
    if (!versionMatch) {
      alert.info("Could not parse version format. Expected format: v1.0");
      return;
    }
    let major = parseInt(versionMatch[1]);
    let minor = parseInt(versionMatch[2]);
    if (isMajorChange) {
      major += 1;
      minor = 0;
    } else {
      minor += 1;
    }
    const newVersion = `v${major}.${minor}`;
    const all_versions = Array.isArray(caption.all_versions) && [
      ...caption.all_versions,
    ];
    if (!all_versions.includes(newVersion)) {
      all_versions.push(newVersion);
    }
    const all_captions = Array.isArray(caption.all_captions)
      ? [...caption.all_captions]
      : [{ version: caption.version || "v1.0" }];
    const newVersionObj = {
      description: caption.description || "",
      version: newVersion,
      fileName: file.name,
    };
    const existingIndex = all_captions.findIndex(
      (v) => v.version === newVersion
    );
    if (existingIndex >= 0) {
      all_captions[existingIndex] = newVersionObj;
    } else {
      all_captions.push(newVersionObj);
    }
    updatedCaptions[captionIndex] = {
      ...caption,
      file: file,
      version: newVersion,
      existingFile: false,
      previousVersion: caption.version,
      fileName: file.name,
      all_versions: all_versions,
      all_captions: all_captions,
    };
    setNewScene((prev) => ({ ...prev, captions: updatedCaptions }));
    handleVersionDialogClose();
  };

  const handleRemoveCaption = (index) => {
    const updatedCaptions = [...newScene.captions];
    updatedCaptions.splice(index, 1);
    setNewScene((prev) => ({ ...prev, captions: updatedCaptions }));
  };

  const fetchCaptionVersions = async (caption, index) => {
    try {
      setLoading(true);
      if (caption.all_versions && Array.isArray(caption.all_versions)) {
        setVersionMenu({
          open: true,
          anchorEl: document.getElementById(`version-button-${index}`),
          captionIndex: index,
          versions: caption.all_versions,
        });
        setLoading(false);
        return;
      }
      const versions = caption.all_versions || ["v1.0", "v1.1", "v2.0"];
      setVersionMenu({
        open: true,
        anchorEl: document.getElementById(`version-button-${index}`),
        captionIndex: index,
        versions: versions,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching caption versions:", error);
      alert.error("Failed to fetch version history. Please try again.");
      setLoading(false);
    }
  };

  const handleVersionMenuClose = () => {
    setVersionMenu({
      open: false,
      anchorEl: null,
      captionIndex: -1,
      versions: [],
    });
  };

  const handleVersionSelect = (version) => {
    const { captionIndex } = versionMenu;
    if (captionIndex === -1) return;
    const updatedCaptions = [...newScene.captions];
    updatedCaptions[captionIndex].version = version;
    setNewScene((prev) => ({
      ...prev,
      captions: updatedCaptions,
    }));
    handleVersionMenuClose();
  };

  const handleSaveNewScene = async () => {
    if (!newScene.scene_name || newScene.captions.length === 0) {
      alert.info("Please fill in all required fields and add at least one caption.");
      return;
    }
    const organizationData = organizations.find(
      (org) => org._id === selectedOrgId
    );
    const organizationName = organizationData?.name?.toLowerCase();
    const sceneId = newScene._id || newScene.id;
    const sceneName = newScene.scene_name?.toLowerCase();

    try {
      setUploading(true);
      const captionsToUpload = newScene.captions.filter(
        (caption) =>
          caption.file && (!caption.existingFile || caption.previousVersion)
      );
      const scenePath = `/scenes/${organizationName.toLowerCase().replace(/\s+/g, "-")}/${sceneName.toLowerCase().replace(/\s+/g, "-")}`;

      for (let i = 0; i < captionsToUpload.length; i++) {
        const caption = captionsToUpload[i];
        if (!caption.file || !caption.caption_name) continue;
        const response = await apiService.uploadFile(caption.file, {
          organizationName: organizationName.toLowerCase().replace(/\s+/g, "-"), 
          sceneName: sceneName.toLowerCase().replace(/\s+/g, "-"),               
          captionName: caption.caption_name,
          version: caption.version
        });
        
        if (!response.success) {
          console.error(
            `Failed to upload file for caption: ${caption.caption_name}`
          );
          continue;
        }
      }
      const apiMethod = isEditing ? "PUT" : "POST";
      const apiUrl = isEditing
        ? `${process.env.REACT_APP_USER_API_URL}/common/scene/${sceneId}`
        : `${process.env.REACT_APP_USER_API_URL}/common/scene`;
       const sceneData = {
      ...(isEditing ? { _id: newScene._id, id: newScene.id } : {}),
      scene_name: newScene.scene_name,
      scene_path: scenePath,
      load_part_path: newScene.load_part_path || null,
      organization_id: selectedOrgId,
      created_by: userInfo.data._id,
      updated_by: userInfo.data._id,
      created_at: new Date(),
      updated_at: new Date(),
      status: "active",
      // OPUS-1391: shared mapper, so a caption field is declared in one place.
      captions: newScene.captions.map(toCaptionPayload),
    };
      if (isEditing) {
        sceneData.captions = sceneData.captions.map(toCaptionPayload);
      }
      
      let savedData;
      if (isEditing) {
        savedData = await apiService.updateScene(sceneId, sceneData);
      } else {
        savedData = await apiService.createScene(sceneData);
      }
      
      try {
        const refreshedData = await apiService.getScenesByOrganization(selectedOrgId);
        if (refreshedData && refreshedData.scenes) {
          setScenes(refreshedData.scenes);
          setSceneOrg(refreshedData.organization);
        }
      } catch (refreshError) {
        console.error("Error refreshing scene list:", refreshError);
        if (isEditing) {
          setScenes((prev) =>
            prev.map((scene) =>
              scene._id === savedData._id ? savedData : scene
            )
          );
        } else {
          setScenes((prev) => [...prev, savedData]);
        }
      }

      trackActivity(
        isEditing ? ACTIVITY_TYPES.UPDATE : ACTIVITY_TYPES.CREATE,
        RESOURCE_TYPES.SCENE,
        savedData._id || savedData.id,
        savedData.scene_name,
        {
          organization_id: selectedOrgId,
          organization_name: organizationData?.name,
          captions_count: newScene.captions.length,
          scene_path: scenePath
        }
      );
      handleClose();
    } catch (error) {
      console.error(`Error ${isEditing ? "updating" : "saving"} scene:`, error);
      alert.error(
        `Failed to ${isEditing ? "update" : "save"} scene. Please try again.`
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDetailsOpen = (scene) => {
    setSelectedScene(scene);
    setDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setSelectedScene(null);
  };

  const handleEditSceneOld = (scene) => {
    setIsEditing(true);
    let sceneData = scene;
    let orgId = null;
    if (scene.organization && scene.scenes) {
      orgId = scene.organization.id;
      sceneData = scene.scenes;
    }
    setNewScene({
      _id: sceneData._id || sceneData.id,
      id: sceneData._id || sceneData.id,
      scene_name: sceneData.name,
      load_part_path: sceneData.load_part_path || null,
      organization_id: selectedOrgId,
      captions: sceneData.captions.map((caption) => {
        const current_version = caption.version;
        let versions = [];
        if (caption.all_captions && Array.isArray(caption.all_captions)) {
          versions = caption.all_captions.map((c) => c.version);
        } else if (
          caption.all_versions &&
          Array.isArray(caption.all_versions)
        ) {
          versions = caption.all_versions
            .map((v) => (typeof v === "string" ? v : v.version || null))
            .filter(Boolean);
        }
        if (!versions.includes(current_version)) {
          versions.push(current_version);
        }
        if (versions.length === 0) {
          versions.push(current_version);
        }
        // OPUS-1391: base fields (incl. disabled + layer) from the shared mapper,
        // then this screen's richer version-history handling on top — it also
        // normalises all_versions entries that arrive as objects rather than
        // strings, which fromApiCaption deliberately leaves alone.
        return {
          ...fromApiCaption(caption),
          version: current_version,
          all_versions: versions,
          all_captions: caption.all_captions || [
            {
              description: caption.description || "",
              version: current_version,
            },
          ],
        };
      }),
    });
    setOpen(true);
  };

 // Keep the existing edit handler
const handleEditScene = (scene) => {
  navigate(`/dashboard/scene-management/${selectedOrgId}/${scene}/edit`);
};

// Add a new handler for navigating to assets page
const handleManageAssets = (scene) => {
  navigate(`/dashboard/scene-management/${selectedOrgId}/${scene}/assets`);
};
  const handleSceneSelection = (e, id) => {
    setSelectedScenes((prev) =>
      e.target.checked ? [...prev, id] : prev.filter((sceneId) => sceneId !== id)
    );
  };

  const handleOpenDeleteDialog = () => {
    if (selectedScenes.length === 0) return;
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const toggleEditMode = () => {
    setEditMode(prev => !prev);
    if (editMode) {
      setSelectedScenes([]);
    }
  };
  const handleBulkDeleteScenes = async () => {
    if (selectedScenes.length === 0) return;

    setDeleteLoading(true);
    try {
      const sceneId = selectedScenes[0];
      const data = await apiService.deleteScene(sceneId);
      
      if (data.linkedRenderers && data.linkedRenderers.length > 0) {
        let rendererList = data.linkedRenderers.map(renderer =>
          `• ${renderer.name} (${renderer.id})`
        ).join('\n');

        alert.error(
          <>
            <strong>{data.message}</strong>
            <div style={{ marginTop: "10px" }}>This scene is mapped to:</div>
            <pre style={{ margin: "8px 0", maxHeight: "150px", overflow: "auto" }}>
              {rendererList}
            </pre>
            <div>Please unmap the scene from these renderers first.</div>
          </>
        );
        return;
      }
      
      const sceneToDelete = scenes.find(scene => (scene._id || scene.id) === sceneId);
      const sceneName = sceneToDelete?.scene_name || sceneToDelete?.name || "Unknown scene";
      
      trackActivity(
        ACTIVITY_TYPES.DELETE,
        RESOURCE_TYPES.SCENE,
        sceneId,
        sceneName,
        {
          organization_id: selectedOrgId,
          organization_name: organizations.find(org => org._id === selectedOrgId)?.name
        }
      );

      setScenes(prevScenes => prevScenes.filter(scene =>
        (scene._id || scene.id) !== sceneId
      ));

      setSelectedScenes([]);

      alert.success("Scene deleted successfully");
    } catch (error) {
      console.error("Error deleting scenes:", error);
      alert.error(error.message || "An error occurred while deleting scenes.");
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteSingleScene = (scene) => {
    setSelectedScenes([scene._id || scene.id]);
    setDeleteDialogOpen(true);
  };
  const fields = [
    {
      key: "captions",
      label: "Captions",
      render: (captions) => {
        if (!captions || captions.length === 0) return "-";
        const maxDisplayCount = 2;
        const remainingCount = captions.length - maxDisplayCount;

        return (
          <>
            {captions.slice(0, maxDisplayCount).map((caption, index) => (
              <Box
                key={caption.id || index}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  mb: 1,
                  p: 1,
                  borderLeft: "2px solid",
                  borderColor: "primary.main",
                  bgcolor: "background.paper",
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1">
                    {caption.caption_name || "Unnamed Caption"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {caption.description || "No description"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                    <Chip
                      size="small"
                      label={`Version: ${caption.version || "v1.0"}`}
                      color="primary"
                      sx={{ mr: 1 }}
                    />
                    {caption.fileName && (
                      <Chip
                        size="small"
                        label={caption.fileName}
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
            {remainingCount > 0 && (
              <Typography variant="body2" color="text.secondary">
                +{remainingCount} more
              </Typography>
            )}
          </>
        );
      },
    },
  ];

  return (
    <ThemeProvider theme={darkTheme}>
      {pageLoading ? (
        <LoadingState rows={8} label="Loading scenes…" />
      ) : (
        <Grid
          container
          spacing={1}
          sx={{ height: '100vh' }}
        >
          <Grid size={12} >

            <DashboardBreadcrumbs
              rightAction={
                <Box sx={{ display: "flex", gap: 1.25 }}>
                  {isSuperAdmin && (
                    <OpusButton variant="secondary" icon="download" onClick={() => { setResumeJob(null); setImportOpen(true); }}>
                      Import from environment
                    </OpusButton>
                  )}
                  <OpusButton variant="primary" icon="plus" onClick={handleClickOpen}>Add Scene</OpusButton>
                </Box>
              }
            />

            <SceneImportDialog
              open={importOpen}
              resumeJob={resumeJob}
              onClose={() => { setImportOpen(false); setResumeJob(null); refreshResumeJobs(); }}
              organizations={organizations}
              onImported={() => {
                if (selectedOrgId) {
                  apiService.getScenesByOrganization(selectedOrgId).then((d) => {
                    if (d && d.scenes) setScenes(d.scenes);
                  }).catch(() => {});
                }
                refreshResumeJobs();
              }}
            />

          </Grid>

          <Grid size={12} sx={{ height: '100%' }}>

            <Box
              sx={{
                height: "calc(100% - 67px)",
                width: "100%",
                p: 2,
                background: "var(--surface-1)",
                border: "1px solid var(--border)",
                borderRadius: "var(--r-lg)",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              {isSuperAdmin && resumeJobs.length > 0 && !resumeDismissed && (
                <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 1, p: "12px 16px", background: "rgba(252,193,68,0.07)", border: "1px solid rgba(252,193,68,0.22)", borderRadius: "var(--r-lg)", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "var(--r-md)", background: "rgba(252,193,68,0.14)", color: "var(--amber)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><OpusIcon name="alertTriangle" size={16} /></Box>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "var(--text)" }}>{resumeJobs.length} import{resumeJobs.length > 1 ? "s" : ""} can be resumed</Typography>
                    <Box sx={{ ml: "auto" }}>
                      <OpusIconButton icon="x" size={15} title="Dismiss" onClick={() => setResumeDismissed(true)} />
                    </Box>
                  </Box>
                  {resumeJobs.map((j) => (
                    <Box key={j._id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, pl: 5.5 }}>
                      <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                        <span style={{ fontFamily: "var(--font-mono)", color: "var(--text)" }}>{j.scene_name}</span> — {j.progress.copied}/{j.progress.total} files copied{j.progress.errored ? `, ${j.progress.errored} failed` : ""}
                      </Typography>
                      <OpusButton variant="secondary" size="sm" icon="refresh" onClick={() => { setResumeJob(j); setImportOpen(true); }}>Resume</OpusButton>
                    </Box>
                  ))}
                </Box>
              )}
              <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.75, mb: 1.5, flexWrap: "wrap" }}>
                {/* OPUS-913: organisation selection moved to the global sidebar selector */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <OpusSearchInput value={sceneSearch} onChange={setSceneSearch} placeholder="Search scenes…" width={260} />
                  <Typography sx={{ fontSize: 13, color: "var(--text-dim)", fontWeight: 600, whiteSpace: "nowrap" }}>{filteredScenes.length} of {scenes.length}</Typography>
                  {/* OPUS-1148: scene-list filters + sort, parity with the renderer list */}
                  <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 13, color: "var(--text-muted)", fontWeight: 600, cursor: "pointer" }}>
                    My scenes <OpusToggle checked={showOnlyMyScenes} onChange={(v) => { setShowOnlyMyScenes(v); setUserToggledSceneFilter(true); }} />
                  </Box>
                  <Box sx={{ minWidth: 160 }}>
                    <OpusSelect size="sm" value={sceneCreatorFilter} onChange={(v) => setSceneCreatorFilter(v)} options={sceneCreatorOptions} />
                  </Box>
                  <Box sx={{ minWidth: 150 }}>
                    <OpusSelect size="sm" value={sceneSortBy} onChange={(v) => setSceneSortBy(v)} options={[
                      { value: "newest", label: "Newest first" },
                      { value: "oldest", label: "Oldest first" },
                      { value: "name", label: "Name (A–Z)" },
                      { value: "creator", label: "Creator" },
                    ]} />
                  </Box>
                </Box>
                <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13, color: "var(--text-muted)", fontWeight: 600, cursor: "pointer" }}>
                  Edit mode <OpusToggle checked={editMode} onChange={() => toggleEditMode()} />
                </Box>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0 }}>
              <SceneList
                scenes={filteredScenes}
                loading={loading}
                editMode={editMode}
                onViewDetails={handleDetailsOpen}
                onEditOld={handleEditSceneOld}
                onEditScene={handleEditScene}
                onManageAssets={handleManageAssets}
                onDeleteScene={handleDeleteSingleScene}
                organizations={organizations}
                selectedOrgId={selectedOrgId}
                selectedScenes={selectedScenes}
                onSceneSelect={handleSceneSelection}
                onUnlockScene={handleUnlockScene}
                onLockScene={handleLockScene}
                canAccessLock={isSuperAdmin}
                onAccessLock={(scene) => setLockModal({ open: true, id: scene._id || scene.id, name: scene.scene_name || scene.name })}
              />
              </Box>

              <SceneForm
                open={open}
                onClose={handleClose}
                isEditing={isEditing}
                newScene={newScene}
                organizations={organizations}
                onSceneChange={handleNewSceneChange}
                onAddCaption={handleAddCaption}
                onCaptionChange={handleCaptionChange}
                onFileChange={handleFileChange}
                onRemoveCaption={handleRemoveCaption}
                onSave={handleSaveNewScene}
                onVersionHistoryClick={fetchCaptionVersions}
                versionMenu={versionMenu}
                onVersionMenuClose={handleVersionMenuClose}
                onVersionSelect={handleVersionSelect}
                uploading={uploading}
                selectedOrgId={selectedOrgId}
              />

              <VersionDialog
                open={versionDialog.open}
                onClose={handleVersionDialogClose}
                caption={
                  versionDialog.captionIndex !== -1
                    ? newScene.captions[versionDialog.captionIndex]
                    : null
                }
                file={versionDialog.file}
                isMajorChange={versionDialog.isMajorChange}
                onMajorChange={handleVersionChangeType}
                onConfirm={handleVersionConfirm}
              />

              <ViewSceneDetails
                open={detailsOpen}
                onClose={handleDetailsClose}
                data={selectedScene}
                fields={fields}
                title="Scene Details"
                theme={darkTheme}
              />
            </Box>

          </Grid>

        </Grid>
      )}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.secondary" }}>
            Are you sure you want to delete  selected scene
            This action cannot be undone and will remove all associated files.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleBulkDeleteScenes} 
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {deleteLoading ? "Deleting..." : "Delete All Selected"}
          </Button>
        </DialogActions>
      </Dialog>
      <AccessLockModal
        open={lockModal.open}
        onClose={() => setLockModal({ open: false, id: null, name: "" })}
        type="scene"
        id={lockModal.id}
        name={lockModal.name}
      />
    </ThemeProvider>
  );
};

export default SceneManagement;
